import React, { useEffect, useState } from 'react';

// Types
import { GoalItem, GoalMetricTypeType } from 'Shared/constants/types';
import { ViewType } from '../GoalProgress/GoalProgress';

// Hooks
import { useGoal } from 'Shared/utils/hooks/use-goal';
import { useTranslation } from 'Shared/utils/hooks/use-translation';
import { useToggle } from 'Shared/utils/hooks';

// Helpers
import { numberToCurrency } from 'Shared/helpers';

// Components
import { WpIcon } from 'Wpds/components/icon';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import { WpTextField } from 'Wpds/components/text-field';
import GoalProgressWrapper from '../GoalProgressWrapper/GoalProgressWrapper';
import GoalSettingsModal, { SettingsType } from '../GoalSettingsModal/GoalSettingsModal';

// Styles
import './GoalMetricValueProgress.scss';

type Props = {
  goal: GoalItem;
  view?: ViewType;
};

const GoalMetricValueProgress = ({ goal, view = 'table' }: Props) => {
  const { tr } = useTranslation();
  const { isProgressPercentage, isProgressDollarAmount, handleUpdateGoal, canChangeStatus } = useGoal({
    slug: goal.slug,
  });

  const [isEditing, toggleIsEditing] = useToggle(false);
  const [isSettingsOpen, toggleIsSettingsOpen] = useToggle(false);

  const [initialValue, setInitialValue] = useState(goal.metricInitialValue.toString());

  const [currentValue, setCurrentValue] = useState(goal.metricCurrentValue.toString());
  const [isSavingCurrentValue, toggleIsSavingCurrentValue] = useToggle(false);

  const [goalValue, setGoalValue] = useState(goal.metricGoalValue.toString());

  const [isResetting, toggleIsResetting] = useToggle(false);

  useEffect(() => {
    if (
      initialValue !== goal.metricInitialValue.toString() ||
      currentValue !== goal.metricCurrentValue.toString() ||
      goalValue !== goal.metricGoalValue.toString()
    ) {
      toggleIsResetting();
      setInitialValue(goal.metricInitialValue.toString());
      setCurrentValue(goal.metricCurrentValue.toString());
      setGoalValue(goal.metricGoalValue.toString());
      setTimeout(() => toggleIsResetting(), 25);
    }
  }, [goal]);

  if (goal.metricInitialValue == null || goal.metricCurrentValue == null || goal.metricGoalValue == null) return null;

  const getFormattedValue = value => {
    switch (goal.metricType) {
      case GoalMetricTypeType.number:
        return value;
      case GoalMetricTypeType.dollar_amount:
        return numberToCurrency(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      case GoalMetricTypeType.percent:
        return `${value}%`;
    }
  };

  const formattedInitialValue = getFormattedValue(goal.metricInitialValue);
  const formattedCurrentValue = getFormattedValue(goal.metricCurrentValue);
  const formattedGoalValue = getFormattedValue(goal.metricGoalValue);

  const percent = Math.round(
    ((goal.metricCurrentValue - goal.metricInitialValue) / (goal.metricGoalValue - goal.metricInitialValue)) * 100
  );

  const handleUpdateCurrentMetric = () => {
    if (currentValue !== goal.metricCurrentValue.toString()) {
      toggleIsSavingCurrentValue();
      handleUpdateGoal({ metric_current_value: currentValue }).then(() => {
        toggleIsSavingCurrentValue();
        toggleIsEditing();
      });
    }
  };

  const handleKeyDownCurrentValue = event => {
    if (event.key === 'Enter' && currentValue !== goal.metricCurrentValue.toString()) {
      handleUpdateCurrentMetric();
    }
  };

  const handleCancelClick = () => {
    setCurrentValue(goal.metricCurrentValue.toString());
    toggleIsEditing();
  };

  const baseClassName = 'GoalMetricValueProgress';

  const label = (
    <>
      <div className={`${baseClassName}__label`}>
        {!isEditing && (
          <div className={`${baseClassName}__label__read`}>
            <div className={`${baseClassName}__label__read__value`}>
              <span className={`${baseClassName}__label__read__value__current`}>{formattedCurrentValue}</span>{' '}
              {!isProgressPercentage && <span>&nbsp;&nbsp;/&nbsp;&nbsp;{percent}%</span>}
            </div>
            {canChangeStatus && (
              <div className={`${baseClassName}__label__read__btn`}>
                <WpButton size="xs" color="gray" slimPadding square onClick={toggleIsEditing}>
                  {tr('perform.goals.progress.update_progress')}
                </WpButton>
              </div>
            )}
            {canChangeStatus && (
              <div className={`${baseClassName}__label__read__edit-btn`}>
                <WpButton square size="xs" variant="tertiary" onClick={toggleIsSettingsOpen}>
                  <WpIcon name="cog" size="sm" />
                </WpButton>
              </div>
            )}
          </div>
        )}
        {isEditing && (
          <div className={`${baseClassName}__label__edit`}>
            <div className={`${baseClassName}__label__edit__input`}>
              <WpTextField
                id="current_value"
                label={tr('perform.goals.progress.current_value_label')}
                labelHidden
                value={currentValue}
                type="number"
                prefixIcon={isProgressDollarAmount && 'dollar-sign'}
                postfixIcon={isProgressPercentage && 'percent'}
                onChange={setCurrentValue}
                onKeyDown={handleKeyDownCurrentValue}
                disabled={isSavingCurrentValue}
                rightAlign={isProgressPercentage}
              />
            </div>
            <div className={`${baseClassName}__label__edit__btn`}>
              <WpButtonGroup>
                <WpButton
                  size="xs"
                  slimPadding
                  square
                  onClick={handleUpdateCurrentMetric}
                  loading={isSavingCurrentValue}
                >
                  {tr('perform.goals.profile.save_button')}
                </WpButton>
                <WpButton
                  size="xs"
                  variant="tertiary"
                  square
                  slimPadding
                  onClick={handleCancelClick}
                  disabled={isSavingCurrentValue}
                >
                  {tr('perform.goals.profile.cancel_button')}
                </WpButton>
              </WpButtonGroup>
            </div>
          </div>
        )}
      </div>
      <GoalSettingsModal
        goal={goal}
        isOpen={isSettingsOpen}
        toggleIsOpen={toggleIsSettingsOpen}
        settingsToDisplay={[SettingsType.progress]}
      />
    </>
  );

  if (isResetting) return null;

  if (view === 'table') return <GoalProgressWrapper percent={percent} />;
  if (view === 'panel')
    return (
      <GoalProgressWrapper
        percent={percent}
        topLabel={{
          label: label,
          size: 'md',
        }}
        bottomLabel={{
          label: (
            <div className={`${baseClassName}__bottom-label`}>
              <span>{formattedInitialValue}</span>
              <span>{formattedGoalValue}</span>
            </div>
          ),
        }}
      />
    );
  if (view === 'page')
    return (
      <GoalProgressWrapper
        percent={percent}
        progressBarSize="md"
        topLabel={{
          label: label,
          size: 'lg',
        }}
        bottomLabel={{
          label: (
            <div className={`${baseClassName}__bottom-label`}>
              <span>{formattedInitialValue}</span>
              <span>{formattedGoalValue}</span>
            </div>
          ),
        }}
      />
    );
};

export default GoalMetricValueProgress;
